<template>
    <div ref="map" id="ol-map-container" class="w-100" :style="{height: mapHeight}"></div>
</template>

<script>
import * as $ from 'jquery';
import {fromLonLat} from "@/olMap/coordUtils";
import OpenLayersMap from "ol/Map";
import {defaults as defaultInteractions} from "ol/interaction";
import {defaults as defaultControls} from "ol/control";
import View from "ol/View";
import ScaleLineControl from "@/olMap/control/ScaleLine";
import MapTypeControl from "@/olMap/control/MapType";
import * as urlUtils from "@/utils/url";
import RoadTypeControl from "@/olMap/control/RoadType";
import {getLayers} from "@/olMap/mapLayers";
import Practice from "@/services/practice";
import VectorLayer from "ol/layer/Vector";
import VectorSource from "ol/source/Vector";
import {Circle, Fill, Icon, Stroke, Style, Text} from "ol/style";
import {
    createFeature, initPlotTypes, labelDirections, labelFont, createPlotStyle, getLabelRect, measureText,
} from "@/olMap/featureUtils";
import * as olExtent from "ol/extent";

export default {
    name: "PracticeExamMap",
    data() {
        return {
            mapHeight: "100vh",
        };
    },
    methods: {
        getClientSize() {
            return [
                window.innerWidth || document.documentElement.clientWidth || document.body.clientWidth,
                window.innerHeight || document.documentElement.clientHeight || document.body.clientHeight,
            ];
        },
        async load(practiceId, detailId) {
            let plots = await Practice.exam(practiceId, detailId);
            plots.forEach(x => x.coordinate = fromLonLat([x.lon, x.lat]));
            this.setViewport(plots.map(x => x.coordinate));
            this.plots = plots;
            this.updatePlotLayer();
        },
        getParams(plot) {
            return {plot: plot, tooltip: plot.label.text, type: plot.label.type};
        },
        createPortFeature(plot, imagePath) {
            return createFeature(plot.coordinate, new Style({
                image: new Icon({src: imagePath, size: [40, 40], anchor: [0.5, 0.9]})
            }), this.getParams(plot));
        },
        createPlotFeature(plot, type) {
            let direction = plot.direction || labelDirections[0];
            let style = createPlotStyle({type, name: plot.label.text}, direction, null, plot.label.color);
            return createFeature(plot.coordinate, style, this.getParams(plot));
        },
        updateLabelDirection() {
            let occupiedRects = [];
            for (let plot of this.plots) {
                let pixel = this.map.getPixelFromCoordinate(plot.coordinate);
                if (pixel) {
                    if (plot.label.type === "start" || plot.label.type === "target") {
                        occupiedRects.push([pixel[0] - 14, pixel[1] - 42, pixel[0] + 14, pixel[1]]);
                    } else if (plot.label.type === "zha") {
                        occupiedRects.push([pixel[0] - 8, pixel[1] - 22, pixel[0] + 8, pixel[1]]);
                    } else {
                        occupiedRects.push([pixel[0] - 8, pixel[1] - 8, pixel[0] + 8, pixel[1] + 8]);
                    }
                }
            }
            for (let plot of this.plots) {
                const isOccupied = rect => {
                    for (let r of occupiedRects) {
                        if (r[2] > rect[0] && rect[2] > r[0] && r[3] > rect[1] && rect[3] > r[1]) {
                            return true;
                        }
                    }
                }

                const tryDirection = direction => {
                    let rect = getLabelRect({
                        coordinate: plot.coordinate, size: [measureText(plot.label.text, labelFont), 28],
                    }, direction, this.map, 9);
                    if (!isOccupied(rect)) {
                        plot.direction = direction;
                        occupiedRects.push(rect);
                        return true;
                    }
                }

                for (let direction of labelDirections) {
                    if (tryDirection(direction)) {
                        break;
                    }
                }
            }
        },
        updatePlotLayer() {
            this.updateLabelDirection();
            let features = [];
            for (let plot of this.plots) {
                if (plot.label.type === "start") {
                    features.push(this.createPortFeature(plot, "image/marker_start_sprite.png"));
                    features.push(this.createPlotFeature(plot, "port"));
                } else if (plot.label.type === "target") {
                    features.push(this.createPortFeature(plot, "image/marker_target_sprite.png"));
                    features.push(this.createPlotFeature(plot, "port"));
                } else if (plot.label.type === "zha") {
                    features.push(this.createPlotFeature(plot, "船闸"));
                } else {
                    features.push(this.createPlotFeature(plot, "answer"));
                }
            }
            this.plotLayer.setSource(new VectorSource({features: features}));
        },
        setViewport(coordinates) {
            if (Array.isArray(coordinates) && coordinates.length > 0) {
                let size = this.getClientSize();
                let px = Math.max(50, Math.round(size[0] * 0.2));
                let py = Math.max(50, Math.round(size[1] * 0.2));
                this.map.getView().fit(olExtent.boundingExtent(coordinates), {
                    padding: [py, px, py, px],
                    maxZoom: 11
                });
            }
        },
    },
    async mounted() {
        this.sizeHandler = () => this.mapHeight = this.getClientSize()[1] + "px";
        window.onresize = this.sizeHandler;
        this.sizeHandler();

        $(document.body).css("overflow", "hidden");

        this.plots = [];
        this.plotLayer = new VectorLayer({source: new VectorSource({features: []})});
        let center = fromLonLat([119.511392, 32.21177]);
        let zoom = 8;
        this.map = new OpenLayersMap({
            target: 'ol-map-container',
            interactions: defaultInteractions({
                altShiftDragRotate: false,
                pinchRotate: false,
                doubleClickZoom: false,
            }),
            controls: defaultControls().extend([
                new ScaleLineControl({units: 'metric'}),
                new MapTypeControl({mapType: urlUtils.getParam("mapType")}),
                new RoadTypeControl({roadType: urlUtils.getParam("roadType")}),
            ]),
            loadTilesWhileInteracting: true,
            view: new View({center, zoom, maxZoom: 19}),
            layers: getLayers().concat([this.plotLayer]),
        });
        this.map.on("moveend", () => this.updatePlotLayer());

        await initPlotTypes([{
            type: "port", width: 16, height: 16, anchorX: 0.5, anchorY: 0.5, nameOffsetX: 7, nameOffsetY: 7
        }, {
            type: "answer", width: 16, height: 16, anchorX: 0.5, anchorY: 0.5, nameOffsetX: 7, nameOffsetY: 7,
            path: "M4 8 a 4,4 0 1,0 8,0 a 4,4 0 1,0 -8,0",
        }]);
        let practiceId = +this.$route.query["practice"] || 0;
        let detailId = +this.$route.query["detail"] || 0;
        await this.load(practiceId, detailId);
    },
    destroyed() {
        $(document.body).css("overflow", "");
    },
}
</script>

<style scoped>

</style>

<style>
.ol-map-type {
    bottom: 10px;
    left: 10px;
}

.ol-road-type {
    bottom: 10px;
    left: 160px;
}

.ol-control.ol-map-type, .ol-control.ol-road-type {
    display: flex;
}

.ol-control.ol-map-type button, .ol-control.ol-road-type button {
    width: auto;
    height: auto;
    line-height: normal;
    font-size: 18px;
    font-weight: normal;
    padding: 5px 10px;
}

.ol-control.ol-map-type button.selected, .ol-control.ol-road-type button.selected {
    text-decoration: none;
    background-color: rgba(0, 60, 136, .8);
}

.ol-scale-line {
    top: auto;
    left: auto;
    right: 10px;
    bottom: 10px;
}

.ol-zoom {
    left: auto;
    top: auto;
    right: 10px;
    bottom: 40px;
}

.ol-zoom button {
    font-size: 1.5rem;
}

@media (max-width: 512px) {
    .ol-road-type {
        bottom: 55px;
        left: 10px;
    }
}

.ol-tooltip {
    position: relative;
    background: rgba(0, 0, 0, 0.5);
    border-radius: 4px;
    color: white;
    padding: 4px 8px;
    opacity: 0.7;
    white-space: nowrap;
    font-size: 12px;
}

.ol-tooltip-measure {
    opacity: 1;
    font-weight: bold;
}

.ol-tooltip-static {
    background-color: orangered;
    color: white;
    border: 1px solid white;
}

.ol-tooltip-measure:before,
.ol-tooltip-static:before {
    border-top: 6px solid rgba(0, 0, 0, 0.5);
    border-right: 6px solid transparent;
    border-left: 6px solid transparent;
    content: "";
    position: absolute;
    bottom: -6px;
    margin-left: -7px;
    left: 50%;
}

.ol-tooltip-static:before {
    border-top-color: orangered;
}

.ol-tooltip-static-close-btn {
    font-size: 1.4rem;
    line-height: 0.8rem;
    height: 1rem;
    cursor: pointer;
}

.ol-tooltip-static-close-btn:hover {
}

.ol-tooltip-static-close-btn:active {
}

.ol-event-transparent {
    pointer-events: none;
}

.ol-event-solid {
    pointer-events: auto;
}

.ol-circle {
    background: transparent;
    width: 10px;
    height: 10px;
    border: 1px solid black;
    border-radius: 5px;
}
</style>
